import { ArrayDataSource } from "@angular/cdk/collections";
import { CdkTree, CdkTreeModule } from "@angular/cdk/tree";
import { NgClass } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { RouterModule } from "@angular/router";
import { IconMenuComponent } from "@optee/icons/icon-menu.component";
import { IconXmarkComponent } from "@optee/icons/icon-xmark.component";
import { ButtonComponent } from "@optee/ui/components/button/button.component";
import type { NavItem } from "../navigation-items";
import { NAV_ITEMS } from "../navigation-items";

@Component({
  selector: "swc-header-mobile",
  host: { class: "relative z-50" },
  template: `
    <header
      class="shadow-o flex h-20 items-center justify-between gap-4 bg-white px-4 sm:gap-x-6 sm:px-6 xl:hidden"
    >
      <button
        class="text-gray-600"
        type="button"
        (click)="isSidebarOpen.set(true)"
      >
        <span class="sr-only">Ouvrir la barre latérale</span>
        <icon-menu class="size-5" aria-true="false" />
      </button>

      <a routerLink="/">
        <span class="sr-only">Optee</span>
        <img
          class="h-8 w-auto"
          alt="Logo de Optee"
          src="/assets/logo-light-theme.svg"
        />
      </a>

      <oui-button href="/demo" variant="primary">Démo</oui-button>
    </header>

    <!-- Overlay -->
    <div
      class="fixed inset-0 bg-gray-600/80 transition-all"
      (click)="isSidebarOpen.set(false)"
      [ngClass]="{ 'pointer-events-none opacity-0': !isSidebarOpen() }"
    ></div>

    <div
      class="fixed left-0 top-0 flex h-full gap-5 transition-all"
      [ngClass]="{
        'pointer-events-none -translate-x-10 opacity-0': !isSidebarOpen(),
      }"
    >
      <!-- Menu -->
      <div class="flex flex-1 flex-col overflow-auto bg-white px-10 py-3">
        <a class="flex h-20 items-center justify-center" routerLink="/">
          <span class="sr-only">Optee</span>
          <img
            class="h-8 w-auto"
            alt="Logo de Optee"
            src="/assets/logo-light-theme.svg"
          />
        </a>

        <nav class="mt-4 flex h-full select-none flex-col">
          <ul
            class="text-primary-700 flex h-full w-60 flex-col gap-y-4"
            role="list"
          >
            <cdk-tree
              class="mt-4 pl-2"
              [childrenAccessor]="childrenAccessor"
              [dataSource]="dataSource"
            >
              <cdk-tree-node
                class="block w-full cursor-pointer py-3 pl-8"
                [routerLink]="node.url"
                *cdkTreeNodeDef="let node"
              >
                {{ node.label }}
              </cdk-tree-node>
              <cdk-tree-node
                class="flex gap-3 py-4"
                cdkTreeNodeToggle
                *cdkTreeNodeDef="let node; when: hasChild"
              >
                {{ node.label }}
              </cdk-tree-node>
            </cdk-tree>
          </ul>
        </nav>
      </div>

      <!-- Close button -->
      <button
        class="text-primary-900 mt-5 flex size-10 items-center justify-center rounded-lg bg-white"
        type="button"
        (click)="isSidebarOpen.set(false)"
      >
        <span class="sr-only">Fermer la barre latérale</span>
        <icon-xmark class="size-5" aria-hidden="true" />
      </button>
    </div>
  `,
  imports: [
    NgClass,
    ButtonComponent,
    IconMenuComponent,
    RouterModule,
    IconXmarkComponent,
    CdkTreeModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class HeaderMobileComponent {
  readonly tree = viewChild(CdkTree);
  readonly currentPage = input.required<string>();

  readonly isSidebarOpen = signal(false);
  readonly dataSource = new ArrayDataSource(NAV_ITEMS);

  childrenAccessor = (dataNode: NavItem) => dataNode.routes ?? [];

  hasChild = (_: number, node: NavItem) =>
    !!node.routes && node.routes.length > 0;

  getParentNode(node: NavItem) {
    for (const parent of this.flattenNodes(NAV_ITEMS)) {
      if (parent.routes?.includes(node)) {
        return parent;
      }
    }

    return null;
  }

  shouldRender(node: NavItem): boolean {
    // This node should render if it is a root node or if all of its ancestors are expanded.
    const parent = this.getParentNode(node);
    return (
      !parent ||
      (!!this.tree()?.isExpanded(parent) && this.shouldRender(parent))
    );
  }

  private flattenNodes(nodes: NavItem[]): NavItem[] {
    const flattenedNodes = [];
    for (const node of nodes) {
      flattenedNodes.push(node);
      if (node.routes) {
        flattenedNodes.push(...this.flattenNodes(node.routes));
      }
    }
    return flattenedNodes;
  }
}
